import type {
  ConversationHandoffRequest,
  ConversationHandoffResult,
} from '@crowdex/agent-contracts/api';
import {
  DEFAULT_EXECUTION_PROFILE_ID,
  DEFAULT_EXECUTION_PROVIDER_ID,
} from '@crowdex/agent-contracts/execution';
import {
  ApiClientError,
  apiClientErrorFromResponse,
  apiFetch,
} from '@crowdex/host-kit/renderer/http';
import type { PullRequestSummary } from '@crowdex/workspace-protocol';
import type { PullRequestCockpit } from '@crowdex/workspace-protocol/pull-request-cockpit';
import { z } from 'zod';
import type { ApiResult } from '@/lib/api';

export const cloudRuntimeSchema = z.object({
  id: z.string().min(1),
  providerId: z.string().min(1).default(DEFAULT_EXECUTION_PROVIDER_ID),
  profileId: z.string().min(1).default(DEFAULT_EXECUTION_PROFILE_ID),
  name: z.string().min(1),
  url: z
    .string()
    .url()
    .refine((url) => url.startsWith('ws://') || url.startsWith('wss://')),
  token: z.string().min(1),
  workspaceRoot: z.string().min(1),
  workspaceIdentity: z.string().min(1),
  agentId: z.string().min(1),
  created: z.boolean().optional().default(false),
  resumed: z.boolean().optional().default(false),
});

export type CloudRuntime = z.infer<typeof cloudRuntimeSchema>;

const cloudRuntimeProvisioningPhaseSchema = z.enum([
  'task-configuration',
  'authorization',
  'sandbox-request',
  'repository-credentials',
  'sandbox-runtime',
  'repository-checkout',
  'finalizing',
]);

export type CloudRuntimeProvisioningPhase = z.infer<typeof cloudRuntimeProvisioningPhaseSchema>;

/** A refusal `not_found` read as `value`; any other failure thrown. */
const notFoundAs =
  <T>(value: T) =>
  (error: unknown): T => {
    if (error instanceof ApiClientError && error.code === 'not_found') return value;
    throw error;
  };

export async function fetchCloudRuntime(
  taskId: string,
  agentId: string | null = 'codex-acp',
  onProgress?: (phase: CloudRuntimeProvisioningPhase) => void,
  target?: { providerId?: string; profileId?: string },
): Promise<CloudRuntime | null> {
  const params = new URLSearchParams({ taskId });
  if (agentId !== null) params.set('agentId', agentId);
  if (target?.providerId) params.set('providerId', target.providerId);
  if (target?.profileId) params.set('profileId', target.profileId);
  params.set('progress', 'stream');
  const response = await fetch(`/api/cloud/runtime?${params.toString()}`, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
  });
  const contentType = response.headers?.get('content-type') ?? '';
  let result: ApiResult<unknown>;
  if (contentType.includes('application/x-ndjson') && response.body !== null) {
    const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
    let buffered = '';
    let streamedResult: ApiResult<unknown> | undefined;
    for (;;) {
      const { done, value } = await reader.read();
      buffered += value ?? '';
      const lines = buffered.split('\n');
      buffered = lines.pop() ?? '';
      for (const line of lines) {
        if (line.length === 0) continue;
        const frame = JSON.parse(line) as { type?: unknown; phase?: unknown; result?: unknown };
        if (frame.type === 'progress') {
          const phase = cloudRuntimeProvisioningPhaseSchema.safeParse(frame.phase);
          if (phase.success) onProgress?.(phase.data);
        } else if (frame.type === 'result') {
          streamedResult = frame.result as ApiResult<unknown>;
        }
      }
      if (done) break;
    }
    if (streamedResult === undefined) throw new Error('Cloud runtime progress stream ended early.');
    result = streamedResult;
  } else {
    if (!response.ok) {
      const refusal = await apiClientErrorFromResponse(response);
      if (refusal.code === 'not_found') return null;
      throw refusal;
    }
    result = (await response.json()) as ApiResult<unknown>;
  }
  if (!result.ok) {
    if (result.error.code === 'not_found') return null;
    throw new Error(result.error.message);
  }
  return cloudRuntimeSchema.parse(result.data);
}

/** Moves a remote conversation to another execution provider (its server does the handoff). */
export async function handoffCloudTaskProvider(
  taskId: string,
  target: { cloud: boolean; providerId: string; profileId: string },
): Promise<ConversationHandoffResult> {
  return await apiFetch<ConversationHandoffResult>('/api/handoffs', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      conversationId: taskId,
      target: {
        cloud: target.cloud,
        placement: { kind: 'remote', providerId: target.providerId, profileId: target.profileId },
      },
    } satisfies ConversationHandoffRequest),
  });
}

export async function snapshotCloudTask(taskId: string): Promise<void> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      await apiFetch(`/api/cloud/tasks/${encodeURIComponent(taskId)}/snapshot`, {
        method: 'POST',
        credentials: 'include',
      });
      return;
    } catch (error) {
      if (!(error instanceof ApiClientError) || error.code !== 'conflict' || attempt === 4)
        throw error;
    }
    await new Promise((resolve) => setTimeout(resolve, 250 * 2 ** attempt));
  }
}

export async function fetchCloudTaskPullRequest(
  taskId: string,
): Promise<PullRequestSummary | null> {
  const data = await apiFetch<{ pullRequest: PullRequestSummary | null }>(
    `/api/source-control/tasks/${encodeURIComponent(taskId)}/pull-request`,
    { credentials: 'include', cache: 'no-store' },
  ).catch(notFoundAs(null));
  return data === null ? null : data.pullRequest;
}

export async function fetchCloudTaskPullRequestCockpit(
  taskId: string,
  signal?: AbortSignal,
): Promise<PullRequestCockpit | null> {
  const data = await apiFetch<{ cockpit: PullRequestCockpit | null }>(
    `/api/source-control/tasks/${encodeURIComponent(taskId)}/pull-request/cockpit`,
    { credentials: 'include', cache: 'no-store', ...(signal === undefined ? {} : { signal }) },
  ).catch(notFoundAs(null));
  return data === null ? null : data.cockpit;
}

export async function mutateCloudTaskPullRequest(
  taskId: string,
  mutation: import('@crowdex/workspace-protocol/pull-request-cockpit').PullRequestMutation,
): Promise<void> {
  await apiFetch(`/api/source-control/tasks/${encodeURIComponent(taskId)}/pull-request/action`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(mutation),
  });
}
